import * as React from 'react'
import ReactDatePicker, {
  ReactDatePickerProps,
  ReactDatePickerCustomHeaderProps,
  registerLocale,
} from 'react-datepicker'
import addDays from 'date-fns/addDays'
import eachYearOfInterval from 'date-fns/eachYearOfInterval'
import getYear from 'date-fns/getYear'
import getMonth from 'date-fns/getMonth'
import eachMonthOfInterval from 'date-fns/eachMonthOfInterval'
import styled, { css } from 'styled-components'

import { DatePickerField } from '../fields/DatePickerField'
import { Dropdown } from '../select/Dropdown'
import { Option } from '../select/Option'

import * as bp from '../../../constants/breakpoints'
import * as colors from '../../../constants/colors'
import * as fonts from '../../../constants/fonts'
import * as glyphs from '../../../icons/glyphs'
import * as S from '../../../styles'
import * as typography from '../../../constants/neue/typography'
import 'react-datepicker/dist/react-datepicker.css'

type Translations = {
  monthsList: string[]
}

export type DatePickerProps = Omit<ReactDatePickerProps, 'selectsRange' | 'onChange'> & {
  error?: React.ReactNode
  initialDate?: Date
  locale: Locale
  onChange?: (dates: Date | Date[]) => void
  placeholder?: { date?: string; time?: string }
  selectsRange?: boolean
  setDateRange?: React.Dispatch<React.SetStateAction<[Date | undefined, Date | undefined]>>
  translations: Translations
  yearInterval?: number
}

type CustomInputProps = {
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void
  value?: string
}

type CustomTimeInputProps = {
  onChange?: (value: string) => void
  value?: string
}

type ControllerDatePickerProps = Omit<ReactDatePickerProps, 'selectsRange' | 'onChange' | 'startDate'> & {
  initialDate?: Date
  locale: Locale
  error?: React.ReactNode
  onChange?: (dates: Date | Date[]) => void
  placeholder?: { date?: string; time?: string }
  selectsRange?: boolean
  setDateRange: React.Dispatch<React.SetStateAction<[Date | undefined, Date | undefined]>>
  startDate?: Date
  translations: Translations
  yearInterval?: number
}

type HeaderProps = ReactDatePickerCustomHeaderProps & {
  locale: Locale
  maxDate: ReactDatePickerProps['maxDate']
  minDate: ReactDatePickerProps['minDate']
  monthsShown?: number
  showMonthYearDropdown?: boolean
  showMonthYearPicker?: boolean
  showQuarterYearPicker?: boolean
  translations: Translations
  yearInterval: number
}

type SelectedMonthYear = {
  disabled: boolean
  handleFocus: () => void
  handleToggle: () => void
  label: string
  value: {
    monthIndex: number
    year: number
  }
}[]

type Selected = {
  disabled: boolean
  handleFocus: () => void
  handleToggle: () => void
  label: string
  value: string
}[]

function DatePickerComponent(props: DatePickerProps): React.ReactElement<DatePickerProps> {
  verifyPropsCompatibility(props)

  if (props.startDate && (props.selectsRange ? props.endDate : true) && props.setDateRange) {
    return <ControlledDatePicker {...props} startDate={props.startDate} setDateRange={props.setDateRange} />
  } else {
    return <UnControlledDatePicker {...props} />
  }
}

function ControlledDatePicker({
  className,
  endDate,
  error,
  locale,
  onChange,
  setDateRange,
  showMonthYearPicker,
  showFullMonthYearPicker = showMonthYearPicker,
  showTimeInput = false,
  showTimeSelectOnly = false,
  startDate,
  translations,
  yearInterval = 5,
  ...reactDatePickerProps
}: ControllerDatePickerProps): React.ReactElement<ControllerDatePickerProps> {
  registerLocale(locale.code ?? 'en', locale)

  // TODO: change this input as a button
  const CustomInput = ({ value, onClick }: CustomInputProps) => (
    <DatePickerField
      onBlur={reactDatePickerProps.onBlur}
      onClick={onClick}
      value={value}
      error={error}
      placeholder={
        reactDatePickerProps.placeholder?.date
          ? reactDatePickerProps.placeholder.date
          : reactDatePickerProps.initialDate
            ? reactDatePickerProps.initialDate.toLocaleDateString()
            : new Date().toLocaleDateString()
      }
      showTimeSelectOnly={showTimeSelectOnly}
      readOnly
    />
  )

  const CustomTimeInput = ({ value, onChange }: CustomTimeInputProps) => (
    <S.input.Input
      onChange={(event) => onChange && onChange(event.target.value)}
      value={value}
      placeholder={
        reactDatePickerProps.placeholder?.time
          ? reactDatePickerProps.placeholder.time
          : reactDatePickerProps.initialDate
            ? reactDatePickerProps.initialDate.toLocaleDateString()
            : new Date().toLocaleDateString()
      }
      type="time"
    />
  )

  return (
    <div className={`${className} datepicker-wrapper`}>
      {/* @ts-expect-error We're forcing access to default since ESM is not supported and an import alias isn't playing nice as well. This will shortly since we're in the process of updating the library. */}
      <ReactDatePicker.default
        customInput={<CustomInput />}
        customTimeInput={<CustomTimeInput />}
        endDate={reactDatePickerProps.selectsRange ? endDate : undefined}
        onChange={
          reactDatePickerProps.selectsRange
            ? (dates: [Date, Date]) => onChangeRange(dates)
            : (date: Date) => onChangeDate(date)
        }
        openToDate={startDate}
        renderCustomHeader={(headerProps: any) => (
          <Header
            locale={locale}
            maxDate={reactDatePickerProps.maxDate}
            minDate={reactDatePickerProps.minDate}
            monthsShown={reactDatePickerProps.monthsShown}
            showMonthYearPicker={showMonthYearPicker}
            showQuarterYearPicker={reactDatePickerProps.showQuarterYearPicker}
            showMonthYearDropdown={reactDatePickerProps.showMonthYearDropdown}
            translations={translations}
            yearInterval={yearInterval}
            {...headerProps}
          />
        )}
        selected={startDate}
        showFullMonthYearPicker={showFullMonthYearPicker}
        showMonthYearPicker={showMonthYearPicker}
        showTimeInput={showTimeInput}
        showTimeSelectOnly={showTimeSelectOnly}
        startDate={startDate}
        locale={locale}
        {...reactDatePickerProps}
      >
        {reactDatePickerProps.children}
        {/* @ts-expect-error We're forcing access to default since ESM is not supported and an import alias isn't playing nice as well. This will shortly since we're in the process of updating the library. */}
      </ReactDatePicker.default>
    </div>
  )

  function onChangeRange(dates: [Date, Date]) {
    setDateRange(dates)
    onChange && onChange(dates)
  }

  function onChangeDate(date: Date) {
    setDateRange([date, undefined])
    onChange && onChange(date)
  }
}

function UnControlledDatePicker(props: DatePickerProps): React.ReactElement<DatePickerProps> {
  const [[startDate, endDate], setDateRange] = React.useState<[Date | undefined, Date | undefined]>([
    props.initialDate,
    undefined,
  ])

  return <ControlledDatePicker {...props} startDate={startDate} endDate={endDate} setDateRange={setDateRange} />
}

function Header({
  changeMonth,
  changeYear,
  customHeaderCount,
  date,
  decreaseMonth,
  increaseMonth,
  locale,
  maxDate,
  minDate,
  monthDate,
  monthsShown,
  nextMonthButtonDisabled,
  prevMonthButtonDisabled,
  showMonthYearDropdown,
  showMonthYearPicker,
  showQuarterYearPicker,
  translations,
  yearInterval,
}: HeaderProps) {
  if (!monthDate) {
    return <></>
  }

  const months = translations.monthsList
  const intervalMonths = eachMonthOfInterval({
    start: minDate ?? new Date(),
    end: maxDate ?? addDays(new Date(), 90),
  })
  const monthByYearOptions: { label: string; value: { monthIndex: number; year: number } }[] = intervalMonths.map(
    (date) => {
      return {
        label: `${months[date.getMonth()]} ${date.getFullYear()}`,
        value: {
          monthIndex: date.getMonth(),
          year: date.getFullYear(),
        },
      }
    },
  )

  const endDateInterval = new Date()
  endDateInterval.setFullYear(endDateInterval.getFullYear() + yearInterval)
  const dateYears: Date[] = eachYearOfInterval({ start: new Date(), end: endDateInterval })
  const alwaysShowControls = monthsShown === undefined || monthsShown < 2

  return (
    <S.datePicker.Header>
      {(alwaysShowControls ? true : customHeaderCount % 2 === 0) && !showQuarterYearPicker && (
        <S.datePicker.PrevButton type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <glyphs.stroke.CaretDown size={18} />
        </S.datePicker.PrevButton>
      )}

      <S.datePicker.HeaderMiddle>
        {customHeaderCount === 0 ? (
          <>
            {showMonthYearDropdown ? (
              <>
                <Dropdown
                  disableScrollLock
                  placeholder={
                    monthByYearOptions.find((option) => {
                      return getMonth(monthDate) === option.value.monthIndex
                    })?.label
                  }
                  selected={[
                    monthByYearOptions.find((option) => getMonth(monthDate) === option.value.monthIndex)?.label,
                  ]}
                  onChange={(selected: SelectedMonthYear) => {
                    changeMonth(selected[0].value.monthIndex)
                    changeYear(selected[0].value.year)
                  }}
                  height={200}
                >
                  {monthByYearOptions.map((option) => {
                    return (
                      <Option key={option.label} value={option.value} label={option.label}>
                        {option.label}
                      </Option>
                    )
                  })}
                </Dropdown>
              </>
            ) : (
              <>
                {!showMonthYearPicker && !showQuarterYearPicker && (
                  <Dropdown
                    disableScrollLock
                    placeholder={months[getMonth(monthDate)]}
                    selected={[months[getMonth(monthDate)]]}
                    onChange={(selected: Selected) => {
                      changeMonth(months.indexOf(selected[0].value))
                    }}
                    height={200}
                  >
                    {months.map((option) => (
                      <Option key={option} value={option} label={option}>
                        {option}
                      </Option>
                    ))}
                  </Dropdown>
                )}

                <Dropdown
                  disableScrollLock
                  placeholder={getYear(date)}
                  onChange={(selected: Selected) => changeYear(Number(selected[0].value))}
                  height={200}
                >
                  {dateYears.map((dateYear) => {
                    const year = getYear(dateYear)

                    return (
                      <Option key={year} value={year} label={String(year)}>
                        {year}
                      </Option>
                    )
                  })}
                </Dropdown>
              </>
            )}
          </>
        ) : (
          <S.datePicker.Label>
            {monthDate.toLocaleString(locale.code, {
              month: 'long',
              year: 'numeric',
            })}
          </S.datePicker.Label>
        )}
      </S.datePicker.HeaderMiddle>

      {(alwaysShowControls ? true : customHeaderCount % 2 !== 0) && !showQuarterYearPicker && (
        <S.datePicker.NextButton type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <glyphs.stroke.CaretDown size={18} />
        </S.datePicker.NextButton>
      )}
    </S.datePicker.Header>
  )
}

export const DatePicker = styled(DatePickerComponent)<DatePickerProps>`
  &.datepicker-wrapper {
    width: fit-content;
  }

  &.datepicker-wrapper .react-datepicker {
    border-radius: 4px;
    border: ${S.datePicker.BORDER};
    display: flex;
    font-family: ${fonts.PRIMARY_FONT};
    width: fit-content;

    &-time {
      &__header {
        ${typography.HEADING_XS};
      }
    }

    &__header {
      background-color: ${colors.PRIMARY_GREY_000};
      border-bottom: 0px;

      &--time:not(.react-datepicker__header--time--only) {
        padding-top: 23px;
        padding-bottom: 19px;
        border-bottom: ${S.datePicker.BORDER};
      }
    }

    &__close {
      &-icon {
        &::after {
          content: '';
          background-color: ${colors.PRIMARY_BLUE};
          /* HACK : inline glyph close  */
          background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 19 19" width="8"><g><path d="M2.7464 4.1606c-.4122-.4122-.4298-1.063-.0393-1.4535.3905-.3905 1.0413-.373 1.4535.0393L16.103 14.6886c.4122.4123.4298 1.063.0392 1.4535-.3905.3906-1.0412.373-1.4535-.0392L2.7464 4.1606Z" fill="white" /><path d="M4.1606 16.1029c-.4122.4122-1.063.4298-1.4535.0392-.3905-.3905-.373-1.0412.0393-1.4535L14.6886 2.7464c.4123-.4122 1.063-.4298 1.4535-.0393.3906.3905.373 1.0413-.0392 1.4535L4.1606 16.1029Z" fill="white" /></g></svg>');
          background-repeat: no-repeat;
          background-position: center;
          transition: background-color ${S.datePicker.TRANSITION};
        }

        &:hover {
          &::after {
            background-color: ${colors.PRIMARY_BLUE_HOVER};
          }
        }
      }
    }

    &__day {
      ${typography.COPY_M_LIGHT};
      background-color: transparent;
      border-radius: 100%;
      border: 1px solid transparent;
      box-sizing: border-box;
      height: 40px;
      line-height: 28px;
      margin: 0px;
      padding: 5px;
      text-align: center;
      transition:
        background-color ${S.datePicker.TRANSITION},
        color ${S.datePicker.TRANSITION};
      vertical-align: bottom;
      width: 40px;

      @media screen and ${bp.MOBILE_L} {
        margin: 3px;
      }

      &--highlighted {
        background-color: transparent;
        color: ${colors.PRIMARY_BLUE};
      }

      &--selected:not(&--in-range) {
        background-color: ${colors.PRIMARY_BLUE};
        color: ${colors.PRIMARY_WHITE};
      }

      &--today {
        border: ${S.datePicker.BORDER};
      }

      &--excluded,
      &--disabled,
      &--outside-month {
        color: ${colors.GREY_SHADE_4};
      }

      &--in-range {
        border-radius: 0;
        background-color: ${colors.BLUE_SHADE_3};
        color: ${colors.PRIMARY_BLUE};

        &.react-datepicker__day--excluded {
          background-color: ${colors.PRIMARY_WHITE};
          color: ${colors.GREY_SHADE_4};
        }
      }

      &--in-selecting-range {
        color: ${colors.PRIMARY_BLUE};
      }

      &-name {
        ${typography.COPY_M_LIGHT};
        color: ${colors.GREY_SHADE_2};
      }

      &-names {
        display: flex;
        justify-content: space-around;
        margin-top: 21px;
      }

      &:hover {
        background-color: ${colors.BLUE_SHADE_3};
        color: ${colors.PRIMARY_BLUE};
      }
    }

    &__input {
      &-container {
        width: fit-content;
      }

      &-time {
        &-container {
          position: absolute;
          bottom: -3px;
          display: flex;
          align-items: center;
          width: fit-content;

          & .react-datepicker-time__caption {
            ${typography.COPY_XS_REGULAR};
          }
        }
      }
    }

    &__month {
      &-text {
        ${typography.COPY_M_LIGHT};
        width: 99px;
        height: 28px;
        text-align: center;
        margin-bottom: 15px;

        &:hover {
          background-color: transparent;
          color: ${colors.PRIMARY_BLUE};
        }

        &--selected,
        &--keyboard-selected {
          color: ${colors.PRIMARY_BLUE};
          background-color: transparent;

          &:hover {
            background-color: transparent;
          }
        }
      }

      &--in-range {
        color: ${colors.PRIMARY_BLUE};
        background-color: transparent;
        /* &--in-selecting-range don't exist for month */
      }

      &--selected {
        color: ${colors.PRIMARY_BLUE};
        background-color: transparent;

        &:hover {
          background-color: transparent;
        }
      }
    }

    &__quarter {
      &-text {
        ${typography.COPY_M_LIGHT};
        width: 99px;
        height: 28px;
        text-align: center;
        margin-bottom: 15px;

        &:hover {
          background-color: transparent;
          color: ${colors.PRIMARY_BLUE};
        }
      }

      &--selected,
      &--in-range {
        color: ${colors.PRIMARY_BLUE};
        background-color: transparent;

        &:hover {
          background-color: transparent;
        }
      }
    }

    &__time {
      &-container,
      &-container .react-datepicker__time .react-datepicker__time-box {
        width: 95px;
      }

      &-container {
        border-left-color: ${S.datePicker.BORDER_COLOR};
      }

      &-list-item {
        ${typography.COPY_M_LIGHT}
        transition: background-color ${S.datePicker.TRANSITION}, color ${S.datePicker.TRANSITION};
        line-height: 28px;

        &--selected {
          background-color: ${colors.PRIMARY_BLUE} !important;
          font-weight: normal !important;
        }

        &:not(&--selected):hover {
          background-color: ${colors.BLUE_SHADE_3} !important;
          color: ${colors.PRIMARY_BLUE};
        }
      }
    }

    &__triangle {
      &::before {
        border-bottom-color: ${S.datePicker.BORDER_COLOR};
      }

      &::after {
        border-bottom-color: ${colors.PRIMARY_WHITE} !important;
      }
    }

    &__week-number {
      ${typography.COPY_S_REGULAR};
      height: 40px;
      line-height: 34px;
      width: 40px;
      color: ${colors.GREY_SHADE_7};
    }

    &__year {
      &-text {
        ${typography.COPY_M_LIGHT};
        width: 85px;
        height: 28px;
        text-align: center;
        margin-bottom: 15px;

        &:hover {
          background-color: transparent;
          color: ${colors.PRIMARY_BLUE};
        }

        &--selected,
        &--keyboard-selected {
          color: ${colors.PRIMARY_BLUE};
          background-color: transparent;

          &:hover {
            background-color: transparent;
          }
        }
      }
    }

    ${({ monthsShown }) => {
      if (monthsShown ? monthsShown >= 2 : false) {
        return css`
          max-width: 300px;
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: hidden;

          @media screen and ${bp.MOBILE_M} {
            max-width: 370px;
          }

          @media screen and ${bp.TABLET} {
            max-width: 671px;
          }

          &__month-container {
            border-right: ${S.datePicker.BORDER};

            &:last-child {
              border-right: none;
            }
          }

          &__time {
            &-container {
              border-left: 0px;
            }
          }
        `
      }
    }}

    ${({ showTimeInput }) => {
      if (showTimeInput) {
        return css`
          padding-bottom: 70px;
        `
      }
    }}
  }
`
DatePicker.displayName = 'DatePicker'

function verifyPropsCompatibility({ ...props }: DatePickerProps) {
  const {
    monthsShown,
    showMonthYearPicker,
    showQuarterYearPicker,
    showTimeSelectOnly,
    showTwoColumnMonthYearPicker,
    showYearPicker,
    selectsRange,
    showPreviousMonths,
    showTimeSelect,
    showWeekNumbers,
    inline,
    isClearable,
    withPortal,
    showTimeInput,
  } = props

  // We have two level of incompatibility
  // error throw errors
  // info just console log the incompatibility
  const errors = []
  const infos = []

  // ERRORS

  if (
    monthsShown &&
    monthsShown > 1 &&
    (showMonthYearPicker ||
      showQuarterYearPicker ||
      showTimeSelectOnly ||
      showTwoColumnMonthYearPicker ||
      showYearPicker)
  ) {
    errors.push(
      "monthsShown can't be set with showMonthYearPicker || showQuarterYearPicker || showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker",
    )
  }

  if (
    selectsRange &&
    (showPreviousMonths || showTimeSelect || showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker)
  ) {
    errors.push(
      "selectsRange can't be set with showPreviousMonths || showTimeSelect || showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker",
    )
  }

  if (showMonthYearPicker && showWeekNumbers) {
    errors.push("showMonthYearPicker can't be set with showWeekNumbers")
  }

  if (inline && (isClearable || withPortal)) {
    errors.push("inline can't be set with isClearable || withPortal")
  }

  if (showQuarterYearPicker && showWeekNumbers) {
    errors.push("showQuarterYearPicker can't be set with showWeekNumbers")
  }

  if (showTimeSelectOnly && showWeekNumbers) {
    errors.push("showTimeSelectOnly can't be set with  showWeekNumbers")
  }

  if (showTwoColumnMonthYearPicker && showWeekNumbers) {
    errors.push("showTwoColumnMonthYearPicker can't be set with showWeekNumbers")
  }

  if (showWeekNumbers && showYearPicker) {
    errors.push("showWeekNumbers can't be set with showYearPicker")
  }

  // INFOS

  if (showMonthYearPicker && (showPreviousMonths || showQuarterYearPicker || showTimeSelectOnly || showYearPicker)) {
    infos.push(
      "showMonthYearPicker can't be set with showPreviousMonths || showQuarterYearPicker || showTimeSelectOnly || showYearPicker",
    )
  }

  if (showPreviousMonths && (showQuarterYearPicker || showTimeSelectOnly || showYearPicker)) {
    infos.push("showPreviousMonths can't be set with showQuarterYearPicker ||  showTimeSelectOnly ||  showYearPicker")
  }

  if (showQuarterYearPicker && (showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker)) {
    infos.push(
      "showQuarterYearPicker can't be set with showTimeSelectOnly || showTwoColumnMonthYearPicker || showYearPicker",
    )
  }

  if (showTimeInput && showTimeSelectOnly) {
    infos.push("showTimeInput can't be set with showTimeSelectOnly")
  }

  if (showTimeSelect && showYearPicker) {
    infos.push("showTimeSelect can't be set with showYearPicker")
  }

  if (showTimeSelectOnly && (showTwoColumnMonthYearPicker || showYearPicker)) {
    infos.push("showTimeSelectOnly can't be set with showTwoColumnMonthYearPicker || showYearPicker")
  }

  if (showTwoColumnMonthYearPicker && showYearPicker) {
    infos.push("showTwoColumnMonthYearPicker can't be set with showYearPicker")
  }

  if (errors.length > 0) {
    throw errors.join(' - ')
  }

  if (infos.length > 0) {
    console.warn(infos.join(' - '))
  }
}
